import axios from 'axios';

import { getResponseData } from '@tankef/api-client/errors';
import { config } from '@tankef/env';

export interface RecoverySessionLocation {
  latitude: number;
  longitude: number;
  address: string;
  city: string;
  state: string;
  country: string;
  isInMexico: boolean;
}

export interface RecoverySessionData {
  email?: string;
  phone?: string;
  location?: RecoverySessionLocation;
}

export interface RecoveryRequestResponse {
  challenge_id?: string;
  message?: string;
}

/**
 * BE wire codes for resume/request 422s (contract: swagger
 * /api/v1/onboarding/sessions/resume/request → 422, pay-service #503/#507).
 *
 * On BOTH codes no OTP was dispatched — the UI must NOT advance to the OTP
 * input; show the BE message with a "contacta a soporte" CTA instead.
 */
export const RESUME_ERROR_CODES = {
  /** Session exists but fell out of the resume window (expires_at + 7d). */
  sessionExpired: 'RESUME-SESSION-EXPIRED',
  /** Max resume count reached for the session. */
  limitExceeded: 'RESUME-LIMIT-EXCEEDED',
} as const;

export interface ResumeRequestError {
  code?: string;
  message?: string;
}

/** Extract `error.code` / `error.message` from a 4xx response body, if present. */
export function getResumeRequestError(error: unknown): ResumeRequestError {
  const body = getResponseData<{ error?: { code?: unknown; message?: unknown } }>(error);
  const code = body?.error?.code;
  const message = body?.error?.message;

  return {
    code: typeof code === 'string' ? code : undefined,
    message: typeof message === 'string' ? message : undefined,
  };
}

/** True when the 422 means "session known but unrecoverable — no OTP sent". */
export function isResumeBlockedError(error: unknown): boolean {
  const { code } = getResumeRequestError(error);
  return code === RESUME_ERROR_CODES.sessionExpired || code === RESUME_ERROR_CODES.limitExceeded;
}

/** `pf` = persona física, `pm` = persona moral. */
export type OnboardingKind = 'pf' | 'pm';

export interface RecoveryConfirmResponse {
  access_token: string;
  /**
   * Tipo de flujo de la sesión reanudada. Es lo que permite mandar al usuario
   * al flujo correcto sin depender del `partyType` en localStorage, que no
   * sobrevive a reanudar desde otro dispositivo o con storage limpio.
   * Opcional: si el BE no lo manda, se cae a `flow_version` y luego al storage.
   */
  kind?: OnboardingKind;
  /** `pf_v3` / `pm_v1`. Alternativa a `kind` para el mismo fin. */
  flow_version?: string;
  /** `Bearer` (OAuth, caduca en minutos) u `onboarding`. */
  token_type?: string;
  /** Segundos de vida real de `access_token`. */
  expires_in?: number;
  /**
   * Token de onboarding (ES256) con la vida de la sesión. Viene mientras el
   * onboarding siga en curso, aunque `access_token` sea Bearer: es el que
   * sirve para seguir la captura sin quedarse sin credenciales a los minutos.
   */
  onboarding_token?: string | null;
  onboarding_token_expires_in?: number | null;
  onboarding_session_id: string;
  current_step: string;
  /** Steps ya cerrados de la sesión. Informativo: el desbloqueo lo dirige `blocks`/`next_step`. */
  completed_steps?: string[];
  progress: number;
  message?: string;
  session_data?: RecoverySessionData;
}

/**
 * Resuelve el `partyType` del FE desde la respuesta del BE. `kind` es la
 * fuente explícita; `flow_version` (`pm_v1`) es el fallback que el BE ya
 * mandaba. `null` cuando no hay señal — el llamador conserva lo que tenga.
 */
export function resolvePartyTypeFromSession(session: {
  kind?: unknown;
  flow_version?: unknown;
}): 'fisica' | 'moral' | null {
  if (session.kind === 'pm' || session.kind === 'legal') return 'moral';
  if (session.kind === 'pf' || session.kind === 'pfae') return 'fisica';
  if (typeof session.flow_version === 'string') {
    if (session.flow_version.startsWith('pm')) return 'moral';
    if (session.flow_version.startsWith('pf')) return 'fisica';
  }
  return null;
}

const sessionRecoveryClient = axios.create({
  baseURL: config.apiUrl,
  timeout: 30000,
});

export async function requestRecoveryOtp(email: string): Promise<RecoveryRequestResponse> {
  const response = await sessionRecoveryClient.post('/api/v1/onboarding/sessions/resume/request', {
    channel: 'email',
    value: email,
  });

  return response.data.data;
}

export async function confirmRecoveryOtp(params: {
  challengeId: string;
  otpCode: string;
}): Promise<RecoveryConfirmResponse> {
  const response = await sessionRecoveryClient.post('/api/v1/onboarding/sessions/resume/confirm', {
    challenge_id: params.challengeId,
    otp_code: params.otpCode,
  });

  return response.data.data;
}
